<script setup lang="ts">
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { h, ref } from 'vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useMinesweeperChallengeLeaderboard } from '@/composables/game/leader-board'
import { useMinesweeperChallenge } from '@/composables/game/challenges'
import { usePlayer } from '@/composables/game/player'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { MINI_GAME_ID } from '@/types/shared/player-config'
import MinesweeperChallengeIcon from '@/assets/images/challenges/minesweeper/minesweeper-challenge-icon.webp'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { useRouter } from 'vue-router'
import { RouteName } from '@/routes'
import { useOutOfSpecialCurrency, usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import MinesweeperChallengeRulesModal from '@/components/modals/MinesweeperChallengeRulesModal.vue'
import BaseChallengeOverlay from '@/components/overlays/BaseChallengeOverlay.vue'
import ClockSvgIcon from '@/assets/images/clock-icon.svg'

defineProps<{
	show: boolean
}>()
const emit = defineEmits<{
	hide: []
	play: [nonce: string]
}>()

const { notify } = useToaster()
const router = useRouter()
const { pathwayEvents, anyActivePathwayEvent } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()
const { isReturningFromGame } = useMinesweeperChallengeModal()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()
const { player, updatePlayer } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const {
  getMinesweeperChallengeLeaderboard,
  minesweeperChallengeLeaderboard: players,
  currentPlayer,
} = useMinesweeperChallengeLeaderboard()
const {
  minesweeperChallenge,
  formattedCountdown,
  entryFee,
  entryCurrency,
  currencyImage,
} = useMinesweeperChallenge()

const buyingEntry = ref(false)
const showRulesModal = ref(false)

async function onCtaClick() {
  addHapticFeedback('medium')
  if (!rawAddress.value) {
    open()
    return
  }

  const playerCurrency = player.value.specialCurrencies[entryCurrency.value] || 0
  if (playerCurrency < entryFee.value) {
    if (canShowOutOfSpecialCurrencyModal(entryCurrency.value)) {
      selectedSpecialCurrencyForModal.value = entryCurrency.value
      return
    }
    notify({
      type: 'error',
      title: `<span class="flex items-center gap-1.5">Not enough <img width="14px" height="14px" style="display: inline-block; vertical-align: middle;" src="${currencyImage.value}" alt="Icon"></span>`,
      description: h('span', { class: 'font-medium' }, [
        h('span', {
          class: 'underline text-teal-primary cursor-pointer',
          onClick: () => {
            if (anyActivePathwayEvent.value) {
              showFirstPathway(pathwayEvents.value)
            } else {
              router.push({ name: RouteName.HOME })
            }
            emit('hide')
          },
        }, 'Explore pathways'),
        ' to get more now!',
      ]),
    })
    return
  }

  buyingEntry.value = true
  try {
    const { playerStats, nonce, error } = await api.buyMiniGameEntry({ gameId: MINI_GAME_ID.MINESWEEPER })

    if (error?.includes('another_entry_in_progress')) {
      notify({
        type: 'error',
        title: 'Purchase Failed',
        description: 'Please wait 1 minute for the next play',
      })
      return
    }

    if (!nonce) {
      throw new Error('No nonce received from server')
    }
    updatePlayer(playerStats)
    const url = `/${MINI_GAME_ID.MINESWEEPER}?nonce=${nonce}`
    window.history.replaceState({}, '', url)
    window.location.reload()
  } catch (error) {
    console.error('Failed to buy minesweeper game entry:', error)
    notify({
      type: 'error',
      title: 'Purchase Failed',
      description: 'Failed to buy minesweeper game entry',
    })
  } finally {
    setTimeout(() => buyingEntry.value = false, 500)
  }
}
</script>

<template>
  <BaseChallengeOverlay
    :show="show"
    :challenge="minesweeperChallenge"
    :formatted-countdown="formattedCountdown"
    :on-cta-click="onCtaClick"
    :get-leaderboard="getMinesweeperChallengeLeaderboard"
    :players="players"
    :current-player="currentPlayer"
    :main-image="MinesweeperChallengeIcon"
    main-image-background-class="bg-gradient-to-br from-[#B8852E] via-[#B8852E] to-black"
    timer-background-class="bg-[linear-gradient(90deg,#B8852E_0%,#950A0A_30.35%,#B8852E_80.77%)]"
    title="Minesweeper"
    description="Clear the grid without hitting mines as fast as you can and climb the leaderboard!"
    cta-text="PLAY"
    :is-rule-card-clickable="true"
    rule-card-line1="Play"
    rule-card-line2="Minesweeper"
    :score-image="ClockSvgIcon"
    :currency-image="currencyImage"
    :challenge-currency="entryCurrency"
    :is-returning-from-game="isReturningFromGame"
    :is-cta-button-disabled="buyingEntry"
    :is-score-in-milliseconds="true"
    @hide="emit('hide')"
    @open-rule-modal="showRulesModal = true"
  >
    <template #ctaButton>
      <template v-if="buyingEntry">
        <span class="text-lg font-bold">PROCESSING...</span>
      </template>
      <template v-else>
        <span class="text-lg font-bold tracking-[1px]">PLAY FOR</span>
        <img
          :src="currencyImage"
          class="size-5 mx-1.5"
          :alt="entryCurrency"
        >
        <span class="text-lg font-bold">{{ entryFee }}</span>
      </template>
    </template>
  </BaseChallengeOverlay>

  <MinesweeperChallengeRulesModal
    :show="showRulesModal"
    @hide="showRulesModal = false"
  />
  <OutOfSpecialCurrencyModal
    v-if="selectedSpecialCurrencyForModal"
    :show="!!selectedSpecialCurrencyForModal"
    :currency="selectedSpecialCurrencyForModal"
    :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
    subtitle="Get more and keep exploring!"
    :has-checkmark="false"
    @hide="closeOutOfSpecialCurrencyModal($event)"
  />
</template>

<style scoped lang="scss">

.balance-pill {
	@apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
	position: relative;
	transition: all 0.3s ease;
}

.scale-in {
	animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
	from {
		transform: scale(0.9);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

.bg-red-green-orange-countdown-banner {
	background: linear-gradient(90deg, #B8852E 0%, #950A0A 30.35%, #B8852E 80.77%);
	box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.52);
}

.scrollable-container {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

.yellow-countdown-banner {
	background: linear-gradient(90deg, #FFD700 0%, #FFA500 100%);
}

.text-shadow-default {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.button-text {
	text-shadow: 0 0 56px rgba(0, 0, 0, 0.50);
}

.pulse-shadow {
	animation: pulseShadow 1.5s ease-in-out infinite;
}

@keyframes pulseShadow {
	0%, 100% {
		box-shadow: 0 0 12px 0 #14F1FF;
	}
	50% {
		box-shadow: 0 0 32px 0 #14F1FF;
	}
}

// Slide-fade transition for arrow
.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
	transform: translateX(-10px);
	opacity: 0;
}

.slide-fade-leave-to {
	transform: translateX(-10px);
	opacity: 0;
}

.content-fade-enter-active,
.content-fade-leave-active {
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-fade-enter-from {
	opacity: 0;
}

.content-fade-leave-to {
	opacity: 0;
}

</style>

<style lang="scss">
@import "@/assets/styles/challenges";
</style>